import { pushDataLayerEvent, trackMetaConversion } from "~/common/tag_scripts";
import { postToEmbedParents } from "~/common/public_form/embed_messaging";
import type { PatientType, Step } from "./types";

// Single home for the booking conversion vocabulary. The event names here are an
// integration contract that marketing setups depend on — keep them in sync with
// features/marketing-attribution/embed-events.md. Every payload must stay
// PII/PHI-free: these reach third-party tag vendors (Google, Meta) and the host
// page's own analytics.

/**
 * Fires on a completed booking, to every configured sink (each no-ops when its
 * tag isn't set up):
 * - in-iframe GTM container — `booking_confirmed` on the dataLayer,
 * - in-iframe Meta Pixel — the standard `Schedule` event (it can't read dataLayer),
 * - the embedding parent page — `booking_complete` for the host site's own GTM.
 *
 * Tracking test mode's "Send test conversion" fires the same events with `test`,
 * marked (`twelveTest` / `twelve_test` / `test: true`) so an agency can exclude it.
 */
export function emitBookingComplete(opts: {
  bookingType: PatientType | "test" | null;
  bookingId: string;
  embedOrigins: string[];
  test?: boolean;
}) {
  pushDataLayerEvent("booking_confirmed", {
    bookingType: opts.bookingType ?? undefined,
    ...(opts.test && { twelveTest: true }),
  });
  trackMetaConversion("Schedule", opts.test ? { twelve_test: true } : undefined);
  postToEmbedParents(opts.embedOrigins, {
    event: "booking_complete",
    bookingId: opts.bookingId,
    ...(opts.test && { test: true }),
  });
}

/**
 * Fires on each booking step view so the host page's tag manager can build a
 * drop-off funnel. Posted to the embedding parent only.
 */
export function emitBookingStep(opts: { step: number; stepName: Step; embedOrigins: string[] }) {
  postToEmbedParents(opts.embedOrigins, {
    event: "booking_step",
    step: opts.step,
    stepName: opts.stepName,
  });
}
